import { I18n } from "@assets/strings";
import React, { JSX } from "react";
import { Link } from "react-router";
import styled from "styled-components";
import { Path } from "@core/routing/path";
import { DetailedPerson, Person } from "@domain/user/user";
import { fullName } from "@domain/user/utils";
import { useIsMobile } from "@utils/hooks";
import { Bloc, Row, Stack } from "@ui/components/layout";
import { LargeTextSkeleton, TextSkeleton } from "@ui/components/skeleton/basicSkeletons";
import { FollowButton } from "./followButton";
import { ImageSize, ProfilePicture, ProfilePictureSkeleton } from "./profilePicture";

interface UserViewProps extends React.HTMLAttributes<HTMLDivElement> {
	user: Person;
	pictureSize?: ImageSize | number;
	renderBottomAccessory?: () => JSX.Element;
	disabled?: boolean;
	gap?: number;
	alignStart?: boolean;
	showDescription?: boolean;
	hidePicture?: boolean;
	followInfos?: DetailedPerson;
	isAuthor?: boolean;
	squareSize?: number;
	inline?: boolean;
	descMaxWidth?: boolean;
	realAuthor?: Person;
	hasBorder?: boolean;
	unClickable?: boolean;
}
export const UserView: React.FC<UserViewProps> = ({ inline, ...props }) => {
	const isMobile = useIsMobile();

	return !inline ? (
		<DefaultUserView {...props} />
	) : !isMobile ? (
		<MobileUserView {...props} />
	) : (
		<InlineUserView {...props} />
	);
};

const DefaultUserView: React.FC<UserViewProps> = ({ unClickable, disabled, ...props }) => {
	const {
		user,
		pictureSize,
		gap,
		renderBottomAccessory,
		alignStart,
		showDescription,
		hidePicture,
		children,
		followInfos,
		isAuthor,
		descMaxWidth,
		realAuthor,
		hasBorder,
		...htmlProps
	} = props;

	return (
		<Container {...htmlProps} disabled={disabled || !user.id}>
			<DefaultUserViewContent {...props} unClickable={unClickable} />
		</Container>
	);
};

const profilePath = (user: Person): string =>
	user.id ? (user.type === "USER" ? Path.Profile(user) : Path.Organization(user)) : "";

const MaybeProfileLink: React.FC<{ user: Person; disabled?: boolean; children: React.ReactNode }> = ({
	user,
	disabled,
	children,
}) => {
	const to = profilePath(user);
	return disabled || !to ? <>{children}</> : <ProfileLink to={to}>{children}</ProfileLink>;
};

const DefaultUserViewContent: React.FC<UserViewProps> = ({
	user,
	pictureSize = "default",
	gap,
	renderBottomAccessory,
	alignStart,
	showDescription,
	hidePicture,
	children,
	followInfos,
	isAuthor,
	descMaxWidth,
	realAuthor,
	hasBorder,
	unClickable,
}) => {
	return (
		<Content gap={gap} alignStart={alignStart}>
			<MaybeProfileLink user={user} disabled={unClickable}>
				<ProfilePicture
					alt={I18n.t("seo.profile.picture", { name: fullName(user) })}
					picture={!hidePicture ? user.avatar : undefined}
					size={pictureSize}
					fallbackType={user.type}
					hasBorder={hasBorder}
				/>
			</MaybeProfileLink>
			<div>
				<div>
					<MaybeProfileLink user={user} disabled={unClickable}>
						<Name className="primaryName">{fullName(user)}</Name>
					</MaybeProfileLink>
					{children}
				</div>
				{showDescription && !realAuthor && (
					<DescriptionWrapper>
						<Description descMaxWidth={descMaxWidth}>{user.description}</Description>
					</DescriptionWrapper>
				)}
				{!!realAuthor && (
					<DescriptionWrapper>
						<Description descMaxWidth={descMaxWidth}>{`${I18n.t("article.thumbnail.from")} ${fullName(
							realAuthor
						)}`}</Description>
					</DescriptionWrapper>
				)}
				{!isAuthor && followInfos && (
					<StyledFollow gap={gap} alignStart={alignStart}>
						{" "}
						<FollowButton isOrg={followInfos.type === "INSTITUTE"} profile={followInfos} />
					</StyledFollow>
				)}
				{renderBottomAccessory?.()}
			</div>
		</Content>
	);
};

const MobileUserView: React.FC<UserViewProps> = ({
	user,
	pictureSize = "default",
	showDescription,
	hidePicture,
	children,
}) => {
	return (
		<Row style={{ margin: 20, marginTop: 10, width: "-webkit-fill-available" }}>
			<Bloc>
				<ProfilePicture
					alt={I18n.t("seo.profile.picture", { name: fullName(user) })}
					picture={!hidePicture ? user.avatar : undefined}
					size={pictureSize}
					fallbackType={user.type}
				/>
			</Bloc>
			<Bloc style={{ marginLeft: 15, marginTop: 20 }}>
				<Row>
					<Name>{fullName(user)}</Name>
				</Row>
				{showDescription && <Row>{user.description}</Row>}
			</Bloc>
			{children}
		</Row>
	);
};

const InlineUserView: React.FC<UserViewProps> = ({
	user,
	pictureSize = "default",
	showDescription,
	hidePicture,
	children,
}) => {
	return (
		<>
			<Row style={{ margin: 20, marginBottom: 0, marginTop: 10 }}>
				<Bloc>
					<ProfilePicture
						alt={I18n.t("seo.profile.picture", { name: fullName(user) })}
						picture={!hidePicture ? user.avatar : undefined}
						size={pictureSize}
						fallbackType={user.type}
					/>
				</Bloc>
				<Bloc style={{ marginLeft: 15, marginTop: 20 }}>
					<Row>
						<Name>{fullName(user)}</Name>
					</Row>
					{showDescription && <Row>{user.description}</Row>}
				</Bloc>
			</Row>
			{children}
		</>
	);
};

export const UserViewSkeleton: React.FC<Omit<UserViewProps, "user">> = ({
	pictureSize,
	gap,
	alignStart,
	...htmlProps
}) => {
	return (
		<Container {...htmlProps}>
			<Content gap={gap} alignStart={alignStart}>
				<ProfilePictureSkeleton size={pictureSize} />
				<TextSkeleton grow height={10} style={{ marginTop: 10 }} />
			</Content>
		</Container>
	);
};

export const CustomUserViewSkeleton: React.FC<Omit<UserViewProps, "user">> = ({
	pictureSize,
	gap,
	alignStart,
	...htmlProps
}) => {
	const isMobile = useIsMobile();
	return (
		<Container {...htmlProps}>
			<Stack gap={gap} align="center" justify="center">
				<ProfilePictureSkeleton size={pictureSize} />
				<LargeTextSkeleton height={6} width={!isMobile ? 40 : 20} />
				<LargeTextSkeleton height={6} width={60} />
			</Stack>
		</Container>
	);
};

const Container = styled.div.withConfig({
	shouldForwardProp: (prop) => prop !== "disabled",
})<{ disabled?: boolean }>`
	pointer-events: ${({ disabled }) => (disabled ? "none" : "all")};
	${({ theme }) => theme.textStyles.text};
`;

const Content = styled.div.withConfig({
	shouldForwardProp: (prop) => !["alignStart"].includes(prop),
})<{ gap?: number; alignStart?: boolean }>`
	display: flex;
	align-items: ${({ alignStart }) => (alignStart ? "flex-start" : "center")};

	> :first-child {
		margin-right: ${({ gap = 7 }) => gap}px;
	}
`;

const StyledFollow = styled.div.withConfig({
	shouldForwardProp: (prop) => !["alignStart"].includes(prop),
})<{ gap?: number; alignStart?: boolean }>`
	display: table;
	table-layout: fixed;
	padding-right: 5px;
	margin-top: 8px;
`;

const ProfileLink = styled(Link)`
	display: inline-flex;
	flex-shrink: 0;
	color: inherit;
`;

const Name = styled.span`
	${({ theme }) => theme.textStyles.secondaryFontFamily};
	word-break: break-word;
`;

const DescriptionWrapper = styled.div`
	width: 100%;
	display: table;
	table-layout: fixed;
	padding-right: 5px;
`;

const Description = styled.div.withConfig({
	shouldForwardProp: (prop) => prop !== "descMaxWidth",
})<{ descMaxWidth?: boolean }>`
	${({ theme }) => theme.textStyles.lighter};
	white-space: nowrap;
	overflow: hidden;
	text-overflow: ellipsis;
	overflow: hidden;
	text-overflow: ellipsis;
	-webkit-line-clamp: 1;
	-webkit-box-orient: horizontal;
	max-width: ${({ descMaxWidth }) => (descMaxWidth ? "30%" : undefined)};
`;
